import { useStore } from '../../../contexts/StoreContext';
import { observer } from 'mobx-react-lite';
import { DynamicModalMap } from '../dynamic-modals/DynamicModal.constants';
import { isPopupModal } from '../dynamic-modals/DynamicModal.utils';
import React from 'react';
import { DynamicModalLayerInstance } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer';
import { useIsFocused } from '@react-navigation/native';

/**
 * This component is a dynamic modal that renders different modal views based on the current dynamicModalView state.
 * It uses the DynamicModalMap to determine which modal view to render.
 *
 * The modal is only visible when the dynamicModalVisible state is true.
 *
 * @returns Array of JSX.Element
 */
export const StackedDynamicModals = observer(() => {
  const {
    dynamicModal: { stackedModals },
  } = useStore();

  const isFocused = useIsFocused();

  return isFocused
    ? stackedModals.reduce((acc: JSX.Element[], modal: DynamicModalLayerInstance, index, array) => {
        const { component: Component, type } = modal?.dynamicModalView && DynamicModalMap[modal.dynamicModalView];
        if (Component) {
          const dynamicModalVisible =
            array.length - 1 === index || // last element is always visible
            (array.length >= 2 && // the penultimate element is visible if the last element is a popup
              array.length - 2 === index &&
              isPopupModal(array[array.length - 1].dynamicModalView));

          const newElement = (
            <Component
              dynamicModalLayer={modal}
              key={modal.dynamicModalView}
              type={type}
              dynamicModalVisible={dynamicModalVisible}
            />
          );
          return [...acc, newElement];
        } else {
          return acc;
        }
      }, [])
    : null;
});
